/**
 * Utility to load external scripts lazily
 * usage: loadScript('https://example.com/script.js', { async: true, defer: true })
 */
export const loadScript = (src: string, attributes: Record<string, string | boolean> = {}) => {
    return new Promise<void>((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = src;

        Object.entries(attributes).forEach(([key, value]) => {
            if (typeof value === 'boolean') {
                if (value) script.setAttribute(key, '');
            } else {
                script.setAttribute(key, value);
            }
        });

        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

        document.body.appendChild(script);
    });
};

/**
 * Hook to load analytics/marketing scripts only after interaction or delay
 */
export const initThirdPartyScripts = () => {
    // The 4s timer and the first-interaction listener are two independent paths
    // into loadScripts. If the timer wins, onInteraction never runs, so its
    // listeners are never removed — the next real click then called loadScripts
    // a second time. loadScript() dedupes the <script> tag, but its .then()
    // still re-ran, pushing a second gtag('config') and double-counting every
    // page_view. Latch it so whichever path fires first is the only one.
    let started = false;
    const loadScripts = () => {
        if (started) return;
        started = true;
        // Google Analytics 4 — loaded lazily to avoid blocking LCP
        // Moved from index.html <head> to reduce render-blocking resources
        //
        // Dual-tagged during the property migration: every hit goes to both
        // the new property and the legacy one, so the new property builds
        // history while the old one keeps reporting uninterrupted. One
        // gtag.js load serves both — the library is per-page, not per-ID, so
        // requesting it once and calling config twice is the supported form.
        // Drop GA_IDS_LEGACY once the new property is trusted.
        const GA_ID = 'G-L5ME7Y8LYF';
        const GA_IDS_LEGACY = ['G-2YJBJ5BDX0'];
        loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`, { async: true })
            .then(() => {
                (window as any).dataLayer = (window as any).dataLayer || [];
                // Must push the `arguments` object, not a rest-parameter array.
                // gtag.js identifies commands by their arguments-object shape;
                // Google's published snippet is `dataLayer.push(arguments)` and
                // a real Array is not that documented contract. The previous
                // rest-array version was off-spec.
                function gtag(..._args: unknown[]) {
                    // eslint-disable-next-line prefer-rest-params
                    (window as any).dataLayer.push(arguments);
                }
                gtag('js', new Date());
                gtag('config', GA_ID);
                GA_IDS_LEGACY.forEach(id => gtag('config', id));
            })
            .catch(err => console.error('GA4 load failed', err));

        // CookieYes — delayed 3s after interaction to avoid blocking LCP/INP
        const cookieYesSrc = 'https://cdn-cookieyes.com/client_data/a46dbe446b41cc5305757fd59e221923/script.js';
        setTimeout(() => {
            loadScript(cookieYesSrc, { id: 'cookieyes' })
                .catch(err => console.error('CookieYes load failed', err));
        }, 3000);

        // Tawk.to — delayed extra 7s after the main batch to avoid competing with INP
        const tawkSrc = 'https://embed.tawk.to/69528956986fc6197b45067c/1jdl6guu8';
        setTimeout(() => {
            loadScript(tawkSrc, { async: true, charset: 'UTF-8', crossorigin: 'anonymous' })
                .catch(err => console.error('Tawk.to load failed', err));
        }, 7000);
    };

    // GA4 loads after 4 seconds or first interaction; Tawk adds another 7s on top
    const timeoutId = setTimeout(loadScripts, 4000);

    const onInteraction = () => {
        clearTimeout(timeoutId);
        loadScripts();
        ['click', 'scroll', 'mousemove', 'touchstart'].forEach(event => {
            window.removeEventListener(event, onInteraction);
        });
    };

    ['click', 'scroll', 'mousemove', 'touchstart'].forEach(event => {
        window.addEventListener(event, onInteraction, { once: true, passive: true });
    });
};
